import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Form, message, Row, Typography } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { leaseService } from "../../../../../configs/constants";
import { Popup, PopupConfirm } from "../../../../atoms/Popup";
import { findSelectOption } from "../../../../libs/form";
import { CommonCard } from "../../components";
import { getBookingDetail, setBookingForm, setFormState, updateBooking } from "../../ducks/actions";
import AdditionalPayments from "../AdditionalPayments";
import RejectPopup from "../modalContent/rejectPopup";
import {
  amount_type,
  apiDateFormat,
  ApprovalHeader,
  buildDepositPaymentsPayload,
  dateFormat,
  filterDropdownValue,
  payment_modes,
  PendingHeader,
} from "../utils";
import AddPlanComm, { rentTypeOptions } from "./AddPlanComm";
import AddTorRules from "./AddTorRules";
import { hasTorRules } from "../../utils";
import { PaymentSchedule, PreviewScheduleGrid } from "./preview-schedule";
import { useGetPaymentTypeOptions } from "../../../../../hooks/useGetPaymentTypeOptions";

const { Text } = Typography;

const initcharge = {
  itemId: null,
  type: "",
  intallments: { label: 1, value: 1 },
  mode: {
    label: "Cheque",
    value: "cheque",
  },
  amount: 0,
  total: 0,
};

const initPlan = {
  //Adding empty because the data is not consistent for Years
};

export default (props) => {
  const { parking } = props;
  const { Title } = Typography;
  const [billingType, setBillingType] = useState([]);
  const [modalvisible, setModalvisible] = useState(false);
  const [modalTrigger, setModalTrigger] = useState(0);
  const [getTorRules, setTorRules] = useState([]);
  const [paymentSchedule, setPaymentSchedule] = useState<PaymentSchedule[]>();
  const [bookingPayments, setBookingPayments] = useState<any[]>([]);
  const [resetBookingPaymentEditTrigger, setResetBookingPaymentEditTrigger] = useState(0);
  const dispatch: any = useDispatch();

  // redux states
  const { bookingForm, bookingDetails, defaultBookingPaymentsAtCreateTime } = useSelector(
    (state: any) => state.newBooking
  );

  const { paymentTypesOptions } = useGetPaymentTypeOptions({
    usageType: bookingDetails?.propertyDetails?.usagesType,
    configKey: "masterPaymentTypeAttributesWithVat",
  });

  const yearDifference =
    moment(bookingForm?.leaseTerms?.leaseEndDate).diff(moment(bookingForm?.leaseTerms?.leaseStartDate), "years") + 1;

  let unitArea = bookingDetails?.propertyDetails?.areaSqft;
  const { leaseId = bookingDetails.leaseId } = useParams();

  const form = useForm({
    defaultValues: {
      plans: [],
      othercharges: [],
      bookingPayments: [],
    },
  });

  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    setValue,
    getValues,
    trigger,
    watch,
    clearErrors,
    reset,
    register,
  } = form;

  const {
    fields: otherChargesFields,
    append: otherChargesAppend,
    remove: otherChargesRemove,
  } = useFieldArray({
    control,
    name: "othercharges",
  });

  const {
    fields: plansFields,
    append: plansAppend,
    remove: plansRemove,
  } = useFieldArray({
    control,
    name: "plans",
  });

  const isEnablePreviewScheduleButton = plansFields.length > 0 || otherChargesFields.length > 0;

  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name) clearErrors(name);
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  const plansWatch = useWatch({ control, name: "plans" });

  const getNextBookingPaymentRowId = () =>
    crypto?.randomUUID?.() || `booking-payment-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const normalizeBookingPayments = (payments = []) =>
    payments.map((payment) => ({
      ...payment,
      __rowId: payment?.__rowId || getNextBookingPaymentRowId(),
    }));

  useEffect(() => {
    if (bookingForm?.bookingTerms?.billingCharges?.length > 0) {
      const billingLines = [];
      bookingForm?.bookingTerms?.billingCharges?.map((item) => {
        let type = filterDropdownValue(item["billingChargesType"], billingType);
        const obj = {
          billingChargesType: { ...type, isDisabled: true },
          intallments: {
            label: item?.numberOfInstallments || 1,
            value: item?.numberOfInstallments || 1,
          },
          dueDate: dateFormat(item?.dueDate),
          modeOfPayment: filterDropdownValue(item?.modeOfPayment || "cheque", payment_modes),
          actualAmount: item?.amountType === "areaSqft" ? item?.unitPerSqAmount : item?.actualAmount,
          vat: item?.vat,
          amountType: filterDropdownValue(item?.amountType, amount_type),
          total: item?.total,
        };
        type && billingLines.push(obj);
      });

      billingLines.length > 0 && setValue("othercharges", billingLines);
    }
  }, [bookingForm, billingType]);

  useEffect(() => {
    setBookingPayments(normalizeBookingPayments(bookingForm?.bookingPayments || defaultBookingPaymentsAtCreateTime || []));
  }, [bookingForm, defaultBookingPaymentsAtCreateTime]);

  const sanitizedDepositPayments = useMemo(() => {
    return buildDepositPaymentsPayload(bookingPayments);
  }, [bookingPayments]);


  useEffect(() => {
    if (!paymentTypesOptions.length) return;

    const disabledValues = new Set(["rent_installment", "security_deposit"]);

    setBillingType(
      paymentTypesOptions.map((item) => ({
        label: item.label,
        value: item.value,
        vatInPercentage: item.vatInPercentage,
        isDisabled: disabledValues.has(item.value),
      }))
    );
  }, [paymentTypesOptions]);

  const isTurnoverRentOnly = bookingDetails?.torType === "turnoverRentOnly";

  useEffect(() => {
    const bookingPlans = bookingForm?.commercialPaymentPlan || bookingForm?.paymentPlan;
    if (bookingPlans?.length > 0) {
      const finalPlans = bookingPlans.map((item) => ({
        ...item,
        installments: { label: item?.installments, value: item?.installments },
        oldInstallments: item?.oldInstallments,
        rentType: rentTypeOptions.find((data) =>
          isTurnoverRentOnly ? data.value === "fixed" : data.value === item?.rentType
        ),
        annualRent: isTurnoverRentOnly ? 0 : item?.annualRent,
      }));

      const currentPlans = getValues("plans");
      const normalizedPlans = isTurnoverRentOnly ? finalPlans.slice(0, 1) : finalPlans;
      if (JSON.stringify(currentPlans) !== JSON.stringify(normalizedPlans)) {
        setValue("plans", normalizedPlans, { shouldDirty: false, shouldValidate: false });
      }
    }
  }, [bookingForm, isTurnoverRentOnly, getValues, setValue]);

  useEffect(() => {
    if (!isTurnoverRentOnly) {
      setValue("plans", []);
      return;
    }

    const currentPlans = getValues("plans") || [];
    if (currentPlans.length > 0) return;

    setValue("plans", [{ rentType: rentTypeOptions.find((data) => data.value === "fixed"), annualRent: 0 }], {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [isTurnoverRentOnly, getValues, setValue]);

  let isleaseTypeTor = bookingForm?.torType ? hasTorRules(bookingForm?.torType) : hasTorRules(bookingDetails?.torType);
  const validateForm = async () => {
    const [plansValid, otherChargesValid] = await Promise.all([
      trigger("plans", { shouldFocus: true }),
      trigger("othercharges", { shouldFocus: true }),
    ]);

    return plansValid && otherChargesValid;
  };

  const prepareFormData = (data?: any, type?: string) => {
    let dataCopy = { ...(data || getValues()) };
    const selectedPlans = (dataCopy?.plans ?? []).map((item) => ({
      ...item,
      installments: item?.installments?.value,
      rentType: item?.rentType?.value,
    }));

    const billingChargesArray = [];
    if (dataCopy?.othercharges?.length > 0) {
      dataCopy?.othercharges?.map((item, index) => {
        const total: any = item?.total;
        billingChargesArray.push({
          billingChargesType: item?.billingChargesType?.value,
          modeOfPayment: item?.modeOfPayment?.value,
          dueDate: apiDateFormat(item?.dueDate),
          numberOfInstallments: item?.intallments?.value || 1,
          amountType: item?.amountType?.value,
          ...(item?.amountType?.value === "areaSqft" ? { unitPerSqAmount: parseFloat(item?.actualAmount) } : {}),
          actualAmount:
            item?.amountType?.value === "areaSqft" ? Number(item?.actualAmount) * Number(unitArea) : parseFloat(item?.actualAmount),
          vat: parseFloat(item?.vat),
          total: parseFloat(total),
        });
      });
    }

    if (selectedPlans.length == 0 && type != "sendPlan") {
      message.info("Please add at least one payment plan to proceed");
      return;
    }

    return {
      commercialPaymentPlan: selectedPlans,
      bookingTerms: {
        ...bookingForm?.bookingTerms,
        billingCharges: billingChargesArray,
      },
      depositPayments: sanitizedDepositPayments,
    };
  };

  const onSubmit = async (data?: any, type?) => {
    setResetBookingPaymentEditTrigger((prev) => prev + 1);
    const isValid = await validateForm();
    if (!isValid) return;

    let body = prepareFormData(data, type);

    if (isleaseTypeTor && getTorRules?.length > 0 && type != "sendPlan") {
      body["torType"] = bookingForm?.torType;
    } else if (
      isleaseTypeTor &&
      (!bookingDetails?.torRules || bookingDetails?.torRules?.length == 0) &&
      type != "sendPlan"
    ) {
      message.info("Please add at least one TOR rule to proceed");
      return;
    }

    let valid = true;
    if (!(await trigger("othercharges", { shouldFocus: true }))) valid = false;
    if (!(await trigger("plans", { shouldFocus: true }))) valid = false;

    if (valid == false) return;

    if (type === "next") {
      if (isValid) {
        dispatch(setFormState());
        dispatch(setBookingForm(body));
      }
    }
      body = { ...bookingForm, ...body };

      props.parentState({ load: true });

      try {
        const response = await dispatch(updateBooking({ leaseId, body }));

        if (type != "sendPlan") await dispatch(getBookingDetail(leaseId));

        message.success(response.data.message);
      } catch (error) {
        message.error(error.response?.data?.error || error.response?.data?.message);
      } finally {
        props.parentState({ load: false });
      }
  };

  const sendPaymentPlan = async () => {
    const isValid = await validateForm();
    if (!isValid) return;

    const addPayments = getValues("plans");

    PopupConfirm({
      title: `Are you sure you want to send plans for approval?`,
      content: "",
      onOk: () => {
        if (addPayments?.length > 0) {
          const finalPayments = addPayments
            .filter((p) => p?.annualRent && p?.annualRent > 0)
            .map((plan) => ({
              ...plan,
              rentType: findSelectOption(rentTypeOptions, plan.rentType?.value),
            }));

          sendPlanApi(
            [...finalPayments],
            [...(bookingDetails?.paymentPlan?.length > 0 ? bookingDetails?.paymentPlan : [])]
          );
        }
      },
      okText: "Confirm",
    });
  };

  const sendPlanApi = async (newPlans, oldPlans) => {
    await onSubmit(null, "sendPlan"); //added fix for bug: 8194
    props.parentState({ load: true });
    const finalData = newPlans?.map((item) => {
      let old = oldPlans.find((plan) => plan.noOfYear == item.noOfYear);
      item = {
        noOfYear: item?.noOfYear,
        installments: item?.installments?.value,
        rentType: item?.rentType?.value,
        unitAreaInSqft: item?.unitAreaInSqft,
        rentValue: item?.rentValue,
        annualRent: item?.annualRent,
        securityDeposit: item?.securityDeposit,

        oldInstallments: old?.installments || item?.installments?.value,
        oldRentType: old?.rentType || item?.rentType?.value,
        oldUnitAreaInSqft: old?.unitAreaInSqft || item?.unitAreaInSqft,
        oldRentValue: old?.rentValue || item?.rentValue,
        oldAnnualRent: old?.annualRent || item?.annualRent,
        oldSecurityDeposit: old?.securityDeposit || item?.securityDeposit,
      };
      return item;
    });
    let body = {
      paymentPlanSendForApproval: "yes",
      paymentPlan: finalData,
      depositPayments: sanitizedDepositPayments,
    };

    try {
      const config: any = {
        data: body,
        method: "PATCH",
        url: `${leaseService}/booking/${leaseId}`,
      };
      const response = await axios(config);
      props.parentState({ load: false });
      message.success(response.data.message);
      await dispatch(getBookingDetail(leaseId));
      reset(null, { keepValues: true });
    } catch (error) {
      props.parentState({ load: false });
      message.error(error.response?.data?.error || error.response?.data?.message);
    }
  };

  const approvePlan = async () => {
    let body = {
      paymentPlanSendForApproval: "approved",
      depositPayments: sanitizedDepositPayments,
    };
    PopupConfirm({
      title: `Are you sure you want to approve payment plans?`,
      content: "",
      onOk: () => approvalPlanApi(body),
      okText: "Confirm",
    });
  };

  const rejectPlan = async (data) => {
    let body = {
      paymentPlanSendForApproval: "rejected",
      rejectionReason: data?.comments ?? "",
      depositPayments: sanitizedDepositPayments,
    };
    setModalvisible(false);
    await approvalPlanApi(body);
  };

  const approvalPlanApi = async (body) => {
    props.parentState({ load: true });
    try {
      const config: any = {
        data: body,
        method: "PATCH",
        url: `${leaseService}/booking/${leaseId}`,
      };
      const response = await axios(config);
      props.parentState({ load: false });
      message.success(response.data.message);
      await dispatch(getBookingDetail(leaseId));
      reset(null, { keepValues: true });
    } catch (error) {
      props.parentState({ load: false });
      message.error(error.response?.data?.error || error.response?.data?.message);
    }
  };

  const addTorRules = () => {
    setModalTrigger(1);
    setModalvisible(true);
  };

  const generatePaymentSchedule = async () => {
    const isValid = await validateForm();
    if (!isValid) return;

    const formData = prepareFormData();
    const body = { ...bookingForm, ...formData };

    props.parentState({ load: true });
    try {
      const response = await dispatch(updateBooking({ leaseId, body }));
      await dispatch(getBookingDetail(leaseId));

      const paymentScheduleData: PaymentSchedule[] = (response?.data?.result?.paymentSchedule || []).map((item) => ({
        installments: item.installments,
        description: item.description,
        actualAmount: item.actualAmount,
        vat: item.vat,
        totalAmount: item.totalAmount,
        dueDate: item.dueDate,
        isWriteOff: item?.isWriteOff,
      }));

      setPaymentSchedule(paymentScheduleData);

      setModalvisible(true);
      setModalTrigger(2);
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message);
    } finally {
      props.parentState({ load: false });
    }
  };

  const popup = [
    {
      title: (
        <Title level={3} className="mb-0">
          Reject Reason
        </Title>
      ),
      width: 650,
      content: <RejectPopup handleReject={rejectPlan} onClose={() => setModalvisible(false)} />,
    },
    {
      title: (
        <Title level={3} className="mb-0">
          {bookingDetails?.torRules?.length > 0 ? "Edit TOR Rules" : "Add TOR Rules"}
        </Title>
      ),
      style: { padding: 0 },
      width: 850,
      content: <AddTorRules setTorRules={setTorRules} onClose={() => setModalvisible(false)} />,
    },
    {
      width: "67rem",
      style: { padding: 0 },
      title: (
        <Title level={3} className="mb-0">
          Preview Schedule
        </Title>
      ),
      content: <PreviewScheduleGrid paymentSchedule={paymentSchedule} />,
    },
  ];

  const popupProps = {
    closable: true,
    visibility: modalvisible,
    title: popup[modalTrigger]?.title,
    content: popup[modalTrigger].content,
    width: popup[modalTrigger].width,
    style: popup[modalTrigger].style,
    onCancel: () => setModalvisible(false),
  };

  const statusHeader = () => {
    // managersManager leaseManager landlord
    if (bookingDetails?.paymentPlanApproval == "sent" && bookingDetails?.loginUserType == "leaseManager") {
      return <PendingHeader />;
    } else if (
      bookingDetails?.paymentPlanApproval == "sent" &&
      (bookingDetails?.loginUserType == "managersManager" || bookingDetails?.loginUserType == "landlord")
    ) {
      return (
        <ApprovalHeader
          approve={approvePlan}
          reject={() => {
            setModalTrigger(0);
            setModalvisible(true);
          }}
        />
      );
    } else return null;
  };

  return (
    <FormProvider {...form}>
      <Form
        layout="vertical"
        name="bookingform"
        className="bf-main"
        scrollToFirstError
        onFinish={handleSubmit(onSubmit)}
      >
        <CommonCard
          label="Payment Plans"
          leftBtnLabel="Back"
          rightBtnLabel="Next"
          handleNext={
            ((!isDirty && bookingDetails?.paymentPlanApproval === "approved") ||
              bookingDetails?.paymentPlanApproval == "disable") &&
            onSubmit
          }
          handleBack={() => dispatch(setFormState("back"))}
          torActions={
            isleaseTypeTor && {
              action: addTorRules,
              label: "Add TOR Rules",
            }
          }
          topButtons={[
            ...(isDirty && bookingDetails?.paymentPlanApproval != "disable"
              ? [{ label: "Send for Approval", action: sendPaymentPlan }]
              : []),
            ...(isEnablePreviewScheduleButton ? [{ label: "Preview Schedule", action: generatePaymentSchedule }] : []),
          ]}
          statusHeader={statusHeader()}
          parentState={props.parentState}
        >
          <Col span={24}>
            <Row gutter={24}>
              <Col span={24}>
                <AddPlanComm
                  fields={plansFields}
                  remove={plansRemove}
                />
              </Col>
              {bookingDetails?.paymentPlanApproval != "sent" &&
                !isTurnoverRentOnly &&
                yearDifference > plansFields.length && (
                <Col span={24}>
                  <Button
                    type="dashed"
                    onClick={() => plansAppend(initPlan)}
                    htmlType="button"
                    icon={<PlusOutlined />}
                    block={true}
                  >
                    <Text className="ag-primary fontWeight600" style={{ fontSize: "14px" }}>
                      Add Payment Plans
                    </Text>
                  </Button>
                </Col>
                )}
              <Col span={24} style={{ marginTop: "20px" }}>
                <p className="ft-main-label">Additional Payments</p>

                <AdditionalPayments
                  fields={otherChargesFields}
                  billingType={billingType}
                  control={control}
                  errors={errors}
                  disableTypeModeDueDate={true}
                  remove={otherChargesRemove}
                  setValue={setValue}
                  disableAll={props.disableAll}
                  getValues={getValues}
                  register={register}
                  // WIP just for set flow later will change
                  unitArea={unitArea || 0}
                  parking={parking}
                  totalAnnualRent={plansWatch?.reduce((a, c) => +(a + c?.annualRent), 0) || null}
                  depositPayments={bookingDetails?.depositPayments}
                  bookingPayments={bookingPayments}
                  onDeleteBookingPayment={(index) => {
                    const updatedFormPayments = (getValues("bookingPayments") || []).filter(
                      (_, currentIndex) => currentIndex !== index
                    );
                    setValue("bookingPayments", updatedFormPayments, { shouldDirty: false, shouldValidate: false });
                    setBookingPayments((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
                  }}
                  onUpdateBookingPayment={(index, updatedPayment) =>
                    setBookingPayments((prev) =>
                      prev.map((payment, currentIndex) =>
                        currentIndex === index
                          ? {
                              ...updatedPayment,
                              __rowId: payment?.__rowId || updatedPayment?.__rowId || getNextBookingPaymentRowId(),
                            }
                          : payment
                      )
                    )
                  }
                  resetBookingPaymentEditTrigger={resetBookingPaymentEditTrigger}
                  dueData={bookingForm?.leaseTerms?.leaseStartDate}
                />

                {!props.disableAll && (
                  <Row gutter={24}>
                    <Col span={24}>
                      <Button
                        type="dashed"
                        // className="p-0"
                        htmlType="button"
                        icon={<PlusOutlined />}
                        block={true}
                        onClick={() => otherChargesAppend(initcharge)}
                      >
                        <Text className="ag-primary fontWeight600" style={{ fontSize: "14px" }}>
                          Add Another Payment
                        </Text>
                      </Button>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </Col>
        </CommonCard>
      </Form>
      <Popup destroyOnClose={true} {...popupProps} />
    </FormProvider>
  );
};
